import {
  DropdownItem,
  showNotification,
  useDialog,
  Dropdown,
  IconButton,
  DeleteDialog,
} from 'platform/components';
import {Show} from 'platform/foundation';
import {Nullish} from 'utility-types';

import {useNavigate, useLocation} from 'react-router';

import {
  InspectionType,
  InspectionTypeLiteral,
  useCloneInspectionMutation,
  useDeleteInspectionMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {vehiclesRoutes} from '@dms/routes';
import {handleApiError} from '@dms/shared';

import {composePath, ReadonlyOrWritable} from 'shared/web';

import {useUpdateInspectionStructure} from '../hooks/useUpdateInspectionStructure';

const REMOVABLE_INSPECTIONS = [
  InspectionType.VALIDATION_INSPECTION,
  InspectionType.NON_VALIDATION_INSPECTION,
  InspectionType.COMPLAINT,
] as const satisfies InspectionType[];

export type InspectionActionType = 'clone' | 'updateStructure' | 'delete';

interface InspectionActionsProps {
  vehicleId: string;
  inspectionId: string;
  inspectionType: InspectionTypeLiteral | Nullish;
  hideActions?: ReadonlyOrWritable<InspectionActionType[]>;
}

export const InspectionActions = (props: InspectionActionsProps) => {
  const navigate = useNavigate();
  const location = useLocation();
  const [isOpen, onOpen, onClose] = useDialog();
  const [deleteInspection, {isLoading: isDeleting}] = useDeleteInspectionMutation({
    fixedCacheKey: 'deleteInspection_auditVideos', // Used to detect deletion of an inspection to not block user navigating when video is being loaded
  });
  const [cloneInspection, {isLoading: isCloning}] = useCloneInspectionMutation();
  const {isLoading: isUpdating, updateInspectionStructure} = useUpdateInspectionStructure(
    props.inspectionId
  );
  const handleDeleteInspection = () => {
    deleteInspection({inspectionId: props.inspectionId, force: true})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('inspection.successfully.deleted'));
        navigate(
          composePath(vehiclesRoutes.conditionsInspections, {
            params: {id: props.vehicleId},
          })
        );
      })
      .catch((error: Error) => {
        showNotification.error(i18n.t('inspection.notSuccessfully.deleted'));
        handleApiError(error);
      });
  };

  const handleCloneInspection = () => {
    cloneInspection({inspectionId: props.inspectionId})
      .unwrap()
      .then(() => {
        showNotification.success(i18n.t('entity.inspection.notifications.cloneStarted'));
      })
      .catch((error: Error) => {
        showNotification.error(i18n.t('entity.inspection.notifications.cloneFailed'));
        handleApiError(error);
      });
  };

  const handleOpenDeleteDialog = () => {
    onOpen();
  };

  const isCloneVisible =
    props.inspectionType &&
    REMOVABLE_INSPECTIONS.includes(props.inspectionType) &&
    !props.hideActions?.includes('clone');
  const isUpdateStructureVisible = !props.hideActions?.includes('updateStructure');
  const isDeleteVisible = !props.hideActions?.includes('delete');

  const isOnInspectionsTab = location.pathname.includes('/inspections');
  const updateStructureTranslationKey = isOnInspectionsTab
    ? 'entity.inspection.labels.updateInspectionStructure'
    : 'entity.inspection.labels.updateTechnicalConditionStructure';

  return (
    <>
      <Dropdown
        placement="bottom-start"
        dropdownControl={<IconButton priority="tertiary" icon="navigation/more_vert" />}
      >
        <Show when={isCloneVisible}>
          <DropdownItem
            label={i18n.t`entity.inspection.labels.cloneInspection`}
            isLoading={isCloning}
            isDisabled={isCloning}
            onClick={handleCloneInspection}
          />
        </Show>
        <Show when={isUpdateStructureVisible}>
          <DropdownItem
            label={i18n.t(updateStructureTranslationKey)}
            isLoading={isUpdating}
            isDisabled={isUpdating}
            onClick={updateInspectionStructure}
          />
        </Show>
        <Show when={isDeleteVisible}>
          <DropdownItem
            label={i18n.t`general.actions.deleteInspection`}
            isLoading={isDeleting}
            isDisabled={isDeleting}
            onClick={handleOpenDeleteDialog}
          />
        </Show>
      </Dropdown>
      <DeleteDialog isOpen={isOpen} onClose={onClose} onConfirm={handleDeleteInspection} />
    </>
  );
};
